import React, { useState } from 'react';
import { compressionAPI } from '../services/api';
import '../styles/CompressionForm.css';

const CompressionForm = ({ onResult, onError, onLoading, loading, onClear }) => {
  const [text, setText] = useState('');
  const [algorithm, setAlgorithm] = useState('HUFFMAN');
  const [operation, setOperation] = useState('compress');

  // For decompression
  const [compressedData, setCompressedData] = useState({
    bitString: '',
    freqMap: '',
    codes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (operation === 'compress' && !text.trim()) {
      onError('Please enter text to compress');
      return;
    }

    if (operation === 'decompress') {
      if (algorithm === 'HUFFMAN' && (!compressedData.bitString || !compressedData.freqMap)) {
        onError('Please provide both bit string and frequency map for Huffman decompression');
        return;
      }
      if (algorithm === 'LZW' && !compressedData.codes) {
        onError('Please provide codes for LZW decompression');
        return;
      }
    }

    onLoading(true);
    onClear();

    try {
      let result;
      
      if (operation === 'compress') {
        result = await compressionAPI.compress(text, algorithm);
        result.originalText = text;
        result.operation = 'compress';
      } else {
        // Prepare decompression request
        const decompressRequest = {
          algorithm: algorithm
        };

        if (algorithm === 'HUFFMAN') {
          decompressRequest.bitString = compressedData.bitString;
          try {
            decompressRequest.freqMap = JSON.parse(compressedData.freqMap);
          } catch (error) {
            throw new Error('Invalid frequency map JSON format');
          }
        } else if (algorithm === 'LZW') {
          try {
            decompressRequest.codes = JSON.parse(compressedData.codes);
            if (!Array.isArray(decompressRequest.codes)) {
              throw new Error('Codes must be an array of numbers');
            }
          } catch (error) {
            throw new Error('Invalid codes JSON format - must be array of numbers');
          }
        }

        result = await compressionAPI.decompress(decompressRequest);
        result.operation = 'decompress';
      }

      onResult(result);
    } catch (error) {
      onError(error.message);
    } finally {
      onLoading(false);
    }
  };

  const handleClear = () => {
    setText('');
    setCompressedData({
      bitString: '',
      freqMap: '',
      codes: ''
    });
    onClear();
  };

  const loadSampleData = () => {
    if (operation === 'compress') {
      setText('banana bandana');
    } else {
      if (algorithm === 'HUFFMAN') {
        setCompressedData({
          bitString: '1010001110100101011110',
          freqMap: '{"b":2,"a":6,"n":4," ":1,"d":1}',
          codes: ''
        });
      } else {
        setCompressedData({
          bitString: '',
          freqMap: '',
          codes: '[98, 97, 110, 256, 257, 32, 259, 100, 260]'
        });
      }
    }
  };

  return (
    <div className="compression-form">
      <div className="form-header">
        <h2>
          <i className={`fas ${operation === 'compress' ? 'fa-compress' : 'fa-expand'}`}></i>
          {operation === 'compress' ? 'Compress Text' : 'Decompress Data'}
        </h2>
        <button 
          type="button" 
          className="sample-btn"
          onClick={loadSampleData}
        >
          Load Sample
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Operation Selection */}
        <div className="form-group">
          <label className="form-label">Operation:</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                value="compress"
                checked={operation === 'compress'}
                onChange={(e) => setOperation(e.target.value)}
              />
              <span>Compress</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                value="decompress"
                checked={operation === 'decompress'}
                onChange={(e) => setOperation(e.target.value)}
              />
              <span>Decompress</span>
            </label>
          </div>
        </div>

        {/* Algorithm Selection */}
        <div className="form-group">
          <label className="form-label">Algorithm:</label>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                value="HUFFMAN"
                checked={algorithm === 'HUFFMAN'}
                onChange={(e) => setAlgorithm(e.target.value)}
              />
              <span>Huffman Coding</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                value="LZW"
                checked={algorithm === 'LZW'}
                onChange={(e) => setAlgorithm(e.target.value)}
              />
              <span>LZW Compression</span>
            </label>
          </div>
        </div>

        {/* Input Fields Based on Operation */}
        {operation === 'compress' ? (
          <div className="form-group">
            <label htmlFor="text" className="form-label">
              Text to Compress:
            </label>
            <textarea
              id="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Enter text to compress..."
              rows="6"
              className="form-textarea"
            />
            <div className="input-info">
              Characters: {text.length}
            </div>
          </div>
        ) : (
          <div className="decompress-inputs">
            {algorithm === 'HUFFMAN' ? (
              <>
                <div className="form-group">
                  <label htmlFor="bitString" className="form-label">
                    Bit String:
                  </label>
                  <textarea
                    id="bitString"
                    value={compressedData.bitString}
                    onChange={(e) => setCompressedData({
                      ...compressedData,
                      bitString: e.target.value
                    })}
                    placeholder="Enter bit string (0s and 1s)..."
                    rows="3"
                    className="form-textarea"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="freqMap" className="form-label">
                    Frequency Map (JSON):
                  </label>
                  <textarea
                    id="freqMap"
                    value={compressedData.freqMap}
                    onChange={(e) => setCompressedData({
                      ...compressedData,
                      freqMap: e.target.value
                    })}
                    placeholder='{"a": 3, "b": 2, "c": 1}'
                    rows="3"
                    className="form-textarea"
                  />
                </div>
              </>
            ) : (
              <div className="form-group">
                <label htmlFor="codes" className="form-label">
                  LZW Codes (JSON Array):
                </label>
                <textarea
                  id="codes"
                  value={compressedData.codes}
                  onChange={(e) => setCompressedData({
                    ...compressedData,
                    codes: e.target.value
                  })}
                  placeholder="[97, 98, 99, 256, 257]"
                  rows="4"
                  className="form-textarea"
                />
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="button-group">
          <button 
            type="submit" 
            className="submit-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Processing...
              </>
            ) : (
              <>
                <i className={`fas ${operation === 'compress' ? 'fa-compress' : 'fa-expand'}`}></i>
                {operation === 'compress' ? 'Compress' : 'Decompress'}
              </>
            )}
          </button>
          
          <button 
            type="button" 
            className="clear-btn"
            onClick={handleClear}
            disabled={loading}
          >
            <i className="fas fa-eraser"></i>
            Clear
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompressionForm;
